import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import { Tooltip } from "react-tooltip";
import { formatDistanceStrict } from "date-fns";
import { onlineManager, useQuery } from "react-query";
import "./treatment.css";
import substrDrugs from "./substrDrugs";
import useMutation from "../../../../hooks/useMutation";
import { formatDate, getLocale } from "../../../../config/i18n";
import { AsyncTypeahead, Hint } from "react-bootstrap-typeahead";

const { REACT_APP_API_URL } = process.env;

const searchVademecum = (country, string) => {
  if (string) {
    // Bug real (auditoría 2026-08-14): URL de prod hardcodeada — un ambiente
    // de QA/staging terminaba pegándole igual a prod. Además, sin chequear
    // res.ok, un error HTTP devolvía data undefined con isError:false,
    // idéntico a "sin resultados". Mismo fix que el resto de la app
    // (REACT_APP_API_URL, ver App/EHR/Form/Table/Cell/Treatments.js).
    return fetch(
      `${REACT_APP_API_URL}/vademecums/${country}?q=${string}`
    ).then((res) => {
      if (!res.ok) throw new Error(`searchVademecum: HTTP ${res.status}`);
      return res.json();
    });
  }
};

// Bug real (auditoría 2026-08-14): `record` se capturaba con un clon
// shallow (`{ ...props.record }`), así que `record.drugs` seguía siendo el
// MISMO array que `props.record.drugs` — el que react-query tiene
// cacheado (ver Treatments.js/`update()`). `onChangeDrug` hacía
// `record.drugs.push(...)` in-place, corrompiendo el cache aunque el
// usuario después cancelara sin guardar. Se clona profundo con la misma
// utilidad que ya usa el resto del código (Consumers/Form/Form.js,
// EHR/V2/Prescriptions/Prescriptions.js) para que las ediciones en curso
// vivan en una copia propia.
let clone = window.structuredClone || JSON.clone;

function Treatment(props) {
  const [countdown, setCountdown] = useState(null);
  const { t } = useTranslation();

  const [record, setRecord] = useState({});
  const [mode, setMode] = useState("read");
  const [tooltipHTML, setTooltipHTML] = useState("");
  const [searchString, setSearchString] = useState("");
  const [prescripted, setPrescripted] = useState(false);

  const [mutate] = useMutation(
    window._l(`/${record.id || "records"}`).substr(1),
    {
      onMutate: (payload) => {
        update(payload);
      },
      onSuccess: (data) => {
        update(data, "clear");
      },
      onError: (error) => {
        window.notify({
          message: error.message,
          type: "danger",
          title: "Error",
        });
      },
    }
  );

  const update = (payload, clear) => {
    setMode("read");
    props.update(payload, clear);
  };

  const onChange = ({ target: { name, value } }) => {
    record[name] = value;
    setRecord({ ...record });
  };

  const onChangeDrug = (array) => {
    // react-bootstrap-typeahead llama onChange([]) cuando se limpia la
    // selección (ej. el usuario clickea la "x" para buscar otra droga) —
    // array[0] queda undefined. Mismo patrón defensivo que
    // Diagnostics/Diagnostic.js#onChangeDx.
    const option = (array || [])[0];
    if (!option) return;

    const next = clone(record);
    if (option.drug) next.drug = option.drug;
    if (option.product) next.product = option.product;
    if (option.product) next.presentation = option.presentation;
    if (option.product) next.company = option.company;
    if (option.product) next.product_code = option.product_code;
    if (option.product) next.drug_code = option.drug_code;
    if (option.drugs)
      option.drugs.forEach((drug) => next.drugs.push(searchReadyDrugs(drug)));
    if (!option.drug && option.label) next.drug = option.label;

    setRecord(next);
  };

  // Bug real (auditoría 2026-08-16): el loop no cortaba al encontrar un match — un match en
  // una iteración temprana se pisaba en silencio por el `else` de una iteración posterior que
  // no matcheaba. Hoy substrDrugs tiene un solo elemento así que no se nota, pero es un bug
  // real de lógica general en cuanto la lista crezca a 2+ entradas. Se reescribe con `.find()`
  // (corta en el primer match) en vez del loop manual sin early exit.
  function searchReadyDrugs(drug) {
    return substrDrugs.find((substr) => drug.search(substr) >= 0) || drug;
  }

  const restore = () => {
    mutate({
      ...record,
      deleted: false,
    });
  };

  const remove = (mode) => {
    setCountdown(null);
    if (!record.id) {
      return update({ ...record, deleted: true, content: "", text: "" });
    }

    if (mode === "confirm") {
      return mutate({
        ...record,
        deleted: true,
      });
    }

    if (mode === "discard") return;

    setCountdown(true);
  };

  const save = async () => {
    mutate({ ...record });
    setMode("read");
  };

  useEffect(() => {
    setRecord(clone(props.record));
  }, [props.record]);

  useEffect(() => {
    setMode(!record.id ? "edit" : "read");
  }, [record.id]);

  let age;
  if (record.createdAt) {
    age = formatDistanceStrict(
      new Date(record.createdAt),
      new Date(Date.now()),
      {
        addSuffix: record.endsAt === "",
        locale: getLocale(),
      }
    );
  }

  let interactions = [];
  if (props.interactions.data && record.drugs) {
    props.interactions.data.forEach((interaction) => {
      record.drugs.forEach((drug) => {
        if (interaction.drugs.includes(drug)) {
          interactions.push(interaction);
        }
      });
    });
  }

  //elimina duplicados: si el farmaco tiene 2 principios activos que interaccionan entre si, se duplica la interaccion en el data-tip
  let noDuplicates = [];
  if (interactions.length > 0) {
    for (const i in interactions) {
      if (
        noDuplicates.findIndex((j) => j.message === interactions[i].message) ===
        -1
      ) {
        noDuplicates.push(interactions[i]);
      }
    }
  }

  const vademecum = useQuery(
    searchString,
    () => searchVademecum(props.country, searchString),
    { initialData: [] }
  );

  let labelledVademecum;

  if (vademecum && vademecum.data) {
    labelledVademecum = vademecum.data.map((element) => ({
      ...element,
      label: `${element.drug} ${element.product} ${element.presentation}`,
    }));
  }

  const prescribeButton = () => {
    return (
      <button
        onClick={() => {
          props.addDrug(record);
          if (prescripted) {
            setPrescripted(false);
          } else {
            setPrescripted(true);
          }
        }}
        className={
          prescripted
            ? "badge  border-0 text-success bg-white ms-0 me-3 align-middle"
            : "badge  border-0 text-danger bg-white ms-0 me-3 align-middle"
        }
      >
        {prescripted ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            className="bi bi-check-circle"
            viewBox="0 0 16 16"
          >
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
            <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z" />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            className="bi bi-circle"
            viewBox="0 0 16 16"
          >
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
          </svg>
        )}
      </button>
    );
  };

  useEffect(() => {
    // Bug real (auditoría 2026-08-24): max-width fijo de 380px en el tooltip
    // de interacciones medicamentosas — en un viewport de 320-375px el
    // tooltip queda más ancho que la pantalla y se recorta contra el borde.
    setTooltipHTML(`
    <div class="text-start" style="max-width: min(380px, calc(100vw - 32px))">
      ${noDuplicates
        .map(
          (i) =>
            `<div>
          <h6>${i.drugs.join(" + ")}: ${i.title} <small class="text-muted">${t(
              i.severity
            )}</small></h6>
        </div>
      `
        )
        .join("")}</div>
  `);
  }, [noDuplicates]);

  return mode === "edit" ? (
    <div className="bg-light p-3 my-3 rounded">
      {/* Bug real (auditoría 2026-08-21): 7 controles (droga, dosis,
          frecuencia, notas, 2 fechas y estado) sin flex-wrap — en mobile la
          fila no entraba y ni los inputs de fecha ni el select se achican
          por debajo de su contenido. */}
      <div className="d-flex align-items-center mb-3 flex-wrap">
        <div className="form-floating flex-fill me-2">
          <AsyncTypeahead
            minLength={2}
            align="left"
            id={record.id || record.createdAt}
            allowNew={(results) => !vademecum.isLoading && results.length === 0}
            isLoading={vademecum.isLoading}
            onChange={onChangeDrug}
            filterBy={() => true}
            options={labelledVademecum}
            onSearch={setSearchString}
            labelKey="label"
            useCache={false}
            emptyLabel={t(`no_matches`, "No hay resultados")}
            paginationText={t(`display_more_results`, "Mostrar mas")}
            promptText={t(`type_to_search`, "Escriba para buscar")}
            searchText={t(`searching`)}
            newSelectionPrefix={null}
            selected={[
              {
                label: record.drug,
                id: record.drug,
              },
            ].filter((d) => d.id)}
            renderInput={({ inputRef, referenceElementRef, ...inputProps }) => (
              <Hint
                shouldSelect={(shouldSelect, e) =>
                  ["Enter", "Tab"].includes(e.key) || shouldSelect
                }
              >
                <div className="form-floating w-100">
                  <input
                    {...inputProps}
                    type="text"
                    autoComplete="off"
                    className="form-control"
                    placeholder={t("ehr:drug_placeholder")}
                    ref={(input) => {
                      inputRef(input);
                      referenceElementRef(input);
                    }}
                  />
                  <label>{t("ehr:drug_placeholder")}</label>
                </div>
              </Hint>
            )}
          ></AsyncTypeahead>
        </div>

        <div className="form-floating flex-fill me-2">
          <input
            value={record.dose}
            onChange={onChange}
            type="text"
            className="form-control"
            id="dose"
            name="dose"
            placeholder={t("dose_placeholder")}
          />
          <label htmlFor="dose">{t("dose")}</label>
        </div>

        <div className="form-floating flex-fill me-2">
          <input
            value={record.frecuency}
            onChange={onChange}
            type="text"
            className="form-control"
            id="frecuency"
            name="frecuency"
            placeholder={t("frecuency_placeholder")}
          />
          <label htmlFor="frecuency">{t("frecuency")}</label>
        </div>

        <div className="form-floating flex-fill me-2">
          <input
            value={record.notes}
            onChange={onChange}
            type="text"
            className="form-control"
            id="notes"
            name="notes"
            placeholder={t("notes_placeholder")}
          />
          <label htmlFor="notes">{t("notes")}</label>
        </div>

        <div className="form-floating flex-fill me-2">
          <input
            value={record.startsAt || ""}
            onChange={onChange}
            type="date"
            className="form-control"
            id="startsAt"
            name="startsAt"
            placeholder={t("startsAt_placeholder")}
          />
          <label htmlFor="startsAt">{t("startsAt")}</label>
        </div>

        <div className="form-floating flex-fill me-2">
          <input
            value={record.endsAt || ""}
            onChange={onChange}
            type="date"
            className="form-control"
            id="endsAt"
            name="endsAt"
            placeholder={t("endsAt_placeholder")}
          />
          <label htmlFor="endsAt">{t("endsAt")}</label>
        </div>

        <div className="form-floating">
          <select
            name="status"
            id="status"
            className="form-select"
            onChange={onChange}
            value={record.status}
          >
            <option value="active">{t("active")}</option>
            <option value="pending">{t("pending")}</option>
            <option value="done">{t("done")}</option>
          </select>
          <label htmlFor="status">{t("status")}</label>
        </div>
      </div>

      {record.deleted ? (
        <div className="d-flex align-items-center">
          <button
            className="btn btn-sm btn-outline-primary me-2"
            onClick={restore}
          >
            {t("restore", { label: t("treatment") })}
          </button>
          <button
            className="btn btn-sm btn-link link-secondary"
            onClick={() => setMode("read")}
          >
            {t("close")}
          </button>
        </div>
      ) : countdown !== null ? (
        <div className="d-flex align-items-center">
          <button
            className="btn btn-sm btn-link link-success me-2"
            onClick={() => remove("discard")}
          >
            {t("discard")}
          </button>
          <button
            className="btn btn-sm btn-link link-danger"
            onClick={() => remove("confirm")}
          >
            {t("confirm")}
          </button>
        </div>
      ) : (
        <div className="d-flex align-items-center">
          <button
            className="btn btn-sm btn-outline-success me-2"
            onClick={save}
          >
            {t("save", { label: t("treatment") })}
          </button>
          <button
            className="btn btn-sm btn-link link-secondary"
            onClick={() => {
              if (!record.updatedAt) {
                setMode("discard");
                mutate({
                  ...record,
                  deleted: true,
                });
              } else {
                setMode("read");
              }
            }}
          >
            {t("close")}
          </button>
          <button className="btn btn-sm btn-link link-danger" onClick={remove}>
            {t("ehr:delete", { label: t("treatment") })}
          </button>
        </div>
      )}
    </div>
  ) : mode === "discard" ? null : (
    <div>
      <div className="mt-3 pt-3 border-top d-flex align-items-center">
        {props.prescribeMode && (
          <div className="d-flex">{prescribeButton()}</div>
        )}
        <div className="d-flex flex-column">
          <strong data-tooltip-id="app-tooltip" data-tooltip-content={record.presentation}>{record.product}</strong>
          <small className="text-muted">{record.drug}</small>
          <div>
            <div>
              {record.label || [record.dose, record.frecuency, record.notes]
                .filter(Boolean)
                .join(", ")}
            </div>
            {noDuplicates.length > 0 && (
              <a
                key={record.id}
                data-background-color="#000"
                data-opactity="1"
                data-tooltip-id={record.id}
                data-tooltip-html={tooltipHTML}
                data-bs-toggle="modal"
                data-bs-target="#interactionModal"
                onClick={() => {
                  props.handleInteractionClick(noDuplicates);
                }}
                className={cx({
                  "text-success btn btn-sm btn-link px-0":
                    noDuplicates[0].severity === "minor",
                  "text-warning btn btn-sm btn-link px-0":
                    noDuplicates[0].severity === "moderate",
                  "text-danger btn btn-sm btn-link px-0":
                    noDuplicates[0].severity === "major",
                })}
              >
                {t("interaction_plural", { count: noDuplicates.length })}
              </a>
            )}
            <Tooltip id={record.id} />
            <div>
              {age && <small className="text-dark me-2">{age}</small>}
              {record.creat && (
                <small className="text-uppercase me-2">
                  {formatDate(record.startsAt, "dd-MMM-yyyy")}
                </small>
              )}
              {record.endsAt && (
                <small className="text-muted me-2">
                  {t("until")} {formatDate(record.endsAt, "dd-MMM-yyyy")}
                </small>
              )}
              <small className="text-muted">
                {record.createdByName}{" "}
                <small>
                  {record.createdByLicense &&
                    record.createdByLicense.replace(/-/g, " ")}
                </small>
              </small>
            </div>
          </div>
        </div>
        <div className="d-flex flex-column ms-auto">
          {record.deleted && (
            <small className="badge bg-danger mx-2 fw-normal">
              {t("ehr:deleted")}
            </small>
          )}
          <small
            className={cx({
              "text-primary mb-3": record.status === "active",
              "text-success mb-3": record.status === "done",
              "text-warning mb-3": record.status === "pending",
            })}
          >
            {t(`pms:${record.status}`)}
          </small>
          <button
            className="btn btn-sm btn-link p-0 align-baseline"
            onClick={() => setMode("edit")}
          >
            {t("edit")}
          </button>
        </div>
      </div>
      <div className="d-flex align-items-center justify-content-between">
        <div></div>
      </div>
    </div>
  );
}

export default Treatment;
